import { useEffect, useMemo, useState } from "react";
import { formatarDataBr, parseDataBr } from "../components/ui/CalendarioCustomizado";

export type TipoPeriodo =
  | "hoje"
  | "esta_semana"
  | "semana_passada"
  | "este_mes"
  | "ano_passado"
  | "este_ano"
  | "tres_meses"
  | "seis_meses"
  | "personalizado";

export type ViewCalendario = "dias" | "meses" | "anos";
type EtapaPersonalizada = "inicio" | "fim";

export const OPCOES_PERIODO: Array<{ tipo: TipoPeriodo; titulo: string }> = [
  { tipo: "hoje", titulo: "Hoje" },
  { tipo: "esta_semana", titulo: "Esta Semana" },
  { tipo: "semana_passada", titulo: "Semana Passada" },
  { tipo: "este_mes", titulo: "Este Mês" },
  { tipo: "ano_passado", titulo: "Ano Passado" },
  { tipo: "este_ano", titulo: "Este Ano" },
  { tipo: "tres_meses", titulo: "3 Meses" },
  { tipo: "seis_meses", titulo: "6 Meses" },
  { tipo: "personalizado", titulo: "Personalizado" },
];

export const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export const MESES_CURTOS = [
  "jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez",
];

export const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];

function limparHora(data: Date): Date {
  const copia = new Date(data);
  copia.setHours(0, 0, 0, 0);
  return copia;
}

function mesmaData(a?: Date | null, b?: Date | null): boolean {
  if (!a || !b) return false;
  return limparHora(a).getTime() === limparHora(b).getTime();
}

function inicioSemana(data: Date): Date {
  const copia = limparHora(data);
  copia.setDate(copia.getDate() - copia.getDay());
  return copia;
}

function fimSemana(data: Date): Date {
  const copia = inicioSemana(data);
  copia.setDate(copia.getDate() + 6);
  return copia;
}

function primeiroDiaMes(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth(), 1);
}

function ultimoDiaMes(data: Date): Date {
  return new Date(data.getFullYear(), data.getMonth() + 1, 0);
}

function obterIntervaloMes(base: Date) {
  return { inicio: primeiroDiaMes(base), fim: ultimoDiaMes(base) };
}

function primeiroDiaAno(data: Date): Date {
  return new Date(data.getFullYear(), 0, 1);
}

function ultimoDiaAno(data: Date): Date {
  return new Date(data.getFullYear(), 11, 31);
}

function subtrairMeses(base: Date, meses: number): Date {
  const dataBase = limparHora(base);
  const ano = dataBase.getFullYear();
  const mes = dataBase.getMonth() - meses;
  const dia = dataBase.getDate();
  const ultimoDiaDestino = new Date(ano, mes + 1, 0).getDate();
  return new Date(ano, mes, Math.min(dia, ultimoDiaDestino));
}

export function obterIntervalo(tipo: Exclude<TipoPeriodo, "personalizado">, base = new Date()) {
  const hoje = limparHora(base);

  if (tipo === "hoje") return { inicio: hoje, fim: hoje };
  if (tipo === "esta_semana") return { inicio: inicioSemana(hoje), fim: fimSemana(hoje) };

  if (tipo === "semana_passada") {
    const inicio = inicioSemana(hoje);
    inicio.setDate(inicio.getDate() - 7);
    const fim = new Date(inicio);
    fim.setDate(fim.getDate() + 6);
    return { inicio, fim };
  }

  if (tipo === "este_mes") return obterIntervaloMes(hoje);

  if (tipo === "ano_passado") {
    const referencia = new Date(hoje.getFullYear() - 1, 0, 1);
    return { inicio: primeiroDiaAno(referencia), fim: ultimoDiaAno(referencia) };
  }

  if (tipo === "este_ano") return { inicio: primeiroDiaAno(hoje), fim: hoje };
  if (tipo === "tres_meses") return { inicio: subtrairMeses(hoje, 3), fim: hoje };
  if (tipo === "seis_meses") return { inicio: subtrairMeses(hoje, 6), fim: hoje };

  return obterIntervaloMes(hoje);
}

function detectarTipoPeriodo(inicio: Date | null, fim: Date | null): TipoPeriodo | null {
  // Ausência de intervalo não equivale ao atalho "Hoje".
  if (!inicio || !fim) return null;

  const hoje = limparHora(new Date());
  const comparacoes: Array<Exclude<TipoPeriodo, "personalizado">> = [
    "hoje", "esta_semana", "semana_passada", "este_mes",
    "ano_passado", "este_ano", "tres_meses", "seis_meses",
  ];

  for (const tipo of comparacoes) {
    const intervalo = obterIntervalo(tipo, hoje);
    if (mesmaData(inicio, intervalo.inicio) && mesmaData(fim, intervalo.fim)) return tipo;
  }

  return "personalizado";
}

function criarGradeDias(referencia: Date): Date[] {
  const ano = referencia.getFullYear();
  const mes = referencia.getMonth();
  const primeiroDia = new Date(ano, mes, 1);
  const inicioGrade = new Date(primeiroDia);
  inicioGrade.setDate(1 - primeiroDia.getDay());

  return Array.from({ length: 42 }, (_, indice) => {
    const data = new Date(inicioGrade);
    data.setDate(inicioGrade.getDate() + indice);
    return data;
  });
}

function parseDataInicial(valor?: string, fallback?: Date | null) {
  const data = parseDataBr(valor);
  if (data) return limparHora(data);
  if (fallback) return limparHora(fallback);
  return null;
}

export function tituloCalendario(view: ViewCalendario, referencia: Date, traduzir: (texto: string) => string) {
  if (view === "dias") return `${traduzir(MESES[referencia.getMonth()])} ${referencia.getFullYear()}`;
  if (view === "meses") return String(referencia.getFullYear());
  const baseDecada = Math.floor(referencia.getFullYear() / 10) * 10;
  return `${baseDecada} - ${baseDecada + 9}`;
}

export function anosDaDecada(referencia: Date) {
  const baseDecada = Math.floor(referencia.getFullYear() / 10) * 10;
  return Array.from({ length: 14 }, (_, indice) => baseDecada - 2 + indice);
}

export function formatarValorCampo(data: Date | null) {
  return data ? formatarDataBr(data) : "DD/MM/AAAA";
}

interface UsePeriodoInterativoOptions {
  ativo: boolean;
  inicio?: string;
  fim?: string;
}

/** Estado e lógica de um seletor de período (atalhos + calendário de intervalo), reutilizado pelo modal global (FiltroPeriodoModal) e pelo modal de filtros avançados. */
export function usePeriodoInterativo({ ativo, inicio, fim }: UsePeriodoInterativoOptions) {
  const [tipo, setTipo] = useState<TipoPeriodo | null>(null);
  const [etapaPersonalizada, setEtapaPersonalizada] = useState<EtapaPersonalizada>("inicio");
  const [viewCalendario, setViewCalendario] = useState<ViewCalendario>("dias");
  const [referenciaCalendario, setReferenciaCalendario] = useState<Date>(() => limparHora(new Date()));
  const [rascunhoInicio, setRascunhoInicio] = useState<Date | null>(null);
  const [rascunhoFim, setRascunhoFim] = useState<Date | null>(null);

  const hoje = useMemo(() => limparHora(new Date()), []);
  const diasCalendario = useMemo(() => criarGradeDias(referenciaCalendario), [referenciaCalendario]);
  const anosCalendario = useMemo(() => anosDaDecada(referenciaCalendario), [referenciaCalendario]);
  const resumoPeriodo = useMemo(() => {
    if (!rascunhoInicio || !rascunhoFim) return "";
    return `${formatarDataBr(rascunhoInicio)} - ${formatarDataBr(rascunhoFim)}`;
  }, [rascunhoFim, rascunhoInicio]);

  useEffect(() => {
    if (!ativo) return;

    const dataInicio = parseDataInicial(inicio);
    const dataFim = parseDataInicial(fim, dataInicio);
    const tipoDetectado = detectarTipoPeriodo(dataInicio, dataFim);

    setTipo(tipoDetectado);
    setEtapaPersonalizada("inicio");
    setViewCalendario("dias");
    setRascunhoInicio(dataInicio);
    setRascunhoFim(dataFim);
    const referencia = dataInicio ?? hoje;
    setReferenciaCalendario(new Date(referencia.getFullYear(), referencia.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo, fim, inicio]);

  function selecionarTipo(proximo: TipoPeriodo) {
    setTipo(proximo);
    setViewCalendario("dias");

    if (proximo === "personalizado") {
      const inicioBase = rascunhoInicio ?? hoje;
      const fimBase = rascunhoFim ?? inicioBase;
      setEtapaPersonalizada("inicio");
      setRascunhoInicio(inicioBase);
      setRascunhoFim(fimBase);
      setReferenciaCalendario(new Date(inicioBase.getFullYear(), inicioBase.getMonth(), 1));
      return;
    }

    const intervalo = obterIntervalo(proximo, new Date());
    setEtapaPersonalizada("inicio");
    setRascunhoInicio(intervalo.inicio);
    setRascunhoFim(intervalo.fim);
    setReferenciaCalendario(new Date(intervalo.inicio.getFullYear(), intervalo.inicio.getMonth(), 1));
  }

  function navegarCalendario(direcao: -1 | 1) {
    setReferenciaCalendario((atual) => {
      if (viewCalendario === "dias") return new Date(atual.getFullYear(), atual.getMonth() + direcao, 1);
      if (viewCalendario === "meses") return new Date(atual.getFullYear() + direcao, atual.getMonth(), 1);
      return new Date(atual.getFullYear() + direcao * 10, atual.getMonth(), 1);
    });
  }

  function alternarViewCalendario() {
    setViewCalendario((atual) => {
      if (atual === "dias") return "meses";
      if (atual === "meses") return "anos";
      return "dias";
    });
  }

  function selecionarMesCalendario(indiceMes: number) {
    setReferenciaCalendario((atual) => new Date(atual.getFullYear(), indiceMes, 1));
    setViewCalendario("dias");
  }

  function selecionarAnoCalendario(ano: number) {
    setReferenciaCalendario((atual) => new Date(ano, atual.getMonth(), 1));
    setViewCalendario("meses");
  }

  function selecionarDiaCalendario(dataDia: Date) {
    const data = limparHora(dataDia);
    const intervaloCompleto = !!rascunhoInicio && !!rascunhoFim;

    if (tipo !== "personalizado" || etapaPersonalizada === "inicio" || !rascunhoInicio || intervaloCompleto) {
      setTipo("personalizado");
      setRascunhoInicio(data);
      setRascunhoFim(null);
      setEtapaPersonalizada("fim");
      return;
    }

    if (rascunhoInicio && data.getTime() < rascunhoInicio.getTime()) {
      setRascunhoInicio(data);
      setRascunhoFim(rascunhoInicio);
      setEtapaPersonalizada("inicio");
      return;
    }

    setRascunhoFim(data);
    setEtapaPersonalizada("inicio");
  }

  function obterResultadoAplicar(): { inicio: string; fim: string } | null {
    if (!rascunhoInicio || !rascunhoFim) return null;
    return { inicio: formatarDataBr(rascunhoInicio), fim: formatarDataBr(rascunhoFim) };
  }

  return {
    tipo,
    etapaPersonalizada,
    viewCalendario,
    referenciaCalendario,
    rascunhoInicio,
    rascunhoFim,
    hoje,
    diasCalendario,
    anosCalendario,
    resumoPeriodo,
    selecionarTipo,
    navegarCalendario,
    alternarViewCalendario,
    selecionarMesCalendario,
    selecionarAnoCalendario,
    selecionarDiaCalendario,
    obterResultadoAplicar,
    podeAplicar: Boolean(rascunhoInicio && rascunhoFim),
  };
}

export { limparHora, mesmaData };
